import { useEffect, useMemo, useRef, useState } from "react";
import { X, Plus, Tag, Info, Copy, Check } from "lucide-react";
import type { Prompt, PromptCategory, PromptCreate, PromptUpdate } from "../api/PromptLibraryApi";
import { extractPlaceholders, isPredefinedPlaceholder, PLACEHOLDER_DEFAULTS, SYSTEM_PLACEHOLDERS } from "./placeholders";

function SystemPlaceholdersPopover({ onClose }: { onClose: () => void }) {
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  async function handleCopy(token: string) {
    await navigator.clipboard.writeText(`{{${token}}}`);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 1500);
  }

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-1.5 z-50 w-80 bg-surface border border-border rounded-xl shadow-xl shadow-black/40 flex flex-col"
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
        <h3 className="text-xs font-semibold text-foreground font-inter">System Placeholders</h3>
        <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground rounded-lg hover:bg-surface-muted">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="max-h-72 overflow-y-auto px-4 py-3 space-y-2.5">
        <p className="text-[11px] text-muted-foreground font-inter">
          No input box is shown for these — they're resolved automatically or pre-filled.
        </p>
        {SYSTEM_PLACEHOLDERS.map((sp) => (
          <div key={sp.token} className="bg-surface-muted/60 border border-border/60 rounded-lg p-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className={`text-[11px] font-mono px-1.5 py-0.5 rounded-full border ${
                sp.kind === "auto"
                  ? "bg-purple-500/10 text-purple-300 border-purple-500/30"
                  : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
              }`}>
                {`{{${sp.token}}}`}
              </span>
              <button
                type="button"
                onClick={() => handleCopy(sp.token)}
                className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground font-inter shrink-0"
              >
                {copiedToken === sp.token ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                {copiedToken === sp.token ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground font-inter mt-1.5">{sp.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

interface PromptFormProps {
  categories: PromptCategory[];
  initial?: Prompt | null;
  defaultCategoryId?: number | null;
  onSave: (data: PromptCreate | PromptUpdate) => Promise<void>;
  onClose: () => void;
}

export default function PromptForm({ categories, initial, defaultCategoryId, onSave, onClose }: PromptFormProps) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const [categoryId, setCategoryId] = useState<number | "">(initial?.category_id ?? defaultCategoryId ?? "");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSystemPlaceholders, setShowSystemPlaceholders] = useState(false);

  const placeholders = useMemo(() => extractPlaceholders(body), [body]);

  function addTag() {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  }

  function removeTag(t: string) {
    setTags(tags.filter((x) => x !== t));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!title.trim()) errs.title = "Title is required";
    if (!body.trim()) errs.body = "Prompt text is required";
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        body,
        category_id: categoryId !== "" ? Number(categoryId) : null,
        tags,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground font-inter">
            {initial ? "Edit Prompt" : "Add Prompt"}
          </h2>
          <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-surface-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Title *</label>
            <input
              value={title}
              onChange={(e) => { setTitle(e.target.value); setErrors((p) => ({ ...p, title: "" })); }}
              className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-inter"
              placeholder="e.g. Generate 5 LinkedIn post topics"
            />
            {errors.title && <p className="text-xs text-destructive mt-1">{errors.title}</p>}
          </div>

          {/* Prompt body */}
          <div>
            <div className="relative flex items-center justify-between mb-1.5">
              <label className="block text-xs font-medium text-muted-foreground font-inter">Prompt Text *</label>
              <button
                type="button"
                onClick={() => setShowSystemPlaceholders((v) => !v)}
                className="flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 font-inter font-medium"
              >
                <Info className="w-3 h-3" />
                System placeholders
              </button>
              {showSystemPlaceholders && <SystemPlaceholdersPopover onClose={() => setShowSystemPlaceholders(false)} />}
            </div>
            <textarea
              rows={8}
              value={body}
              onChange={(e) => { setBody(e.target.value); setErrors((p) => ({ ...p, body: "" })); }}
              className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-mono resize-y"
              placeholder={"Write the standard prompt here. Use {{placeholder}} for parts the employee should fill in, e.g.\n\nWrite a LinkedIn comment about {{topic}} for someone in the {{industry}} industry."}
            />
            {errors.body && <p className="text-xs text-destructive mt-1">{errors.body}</p>}
            {placeholders.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <span className="text-[11px] text-muted-foreground font-inter">Placeholders detected:</span>
                {placeholders.map((p) => {
                  const isSystem = isPredefinedPlaceholder(p);
                  const hasDefault = p in PLACEHOLDER_DEFAULTS;
                  return (
                    <span key={p} className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                      {`{{${p}}}`}
                      {isSystem && <span className="text-purple-300"> · auto</span>}
                      {hasDefault && <span className="text-emerald-300"> · default</span>}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Category</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value === "" ? "" : Number(e.target.value))}
              className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary font-inter"
            >
              <option value="">Uncategorised</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Tags</label>
            <div className="flex gap-2 mb-2 flex-wrap">
              {tags.map((t) => (
                <span key={t} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs font-mono border border-primary/30">
                  <Tag className="w-3 h-3" />
                  {t}
                  <button type="button" onClick={() => removeTag(t)} className="ml-0.5 hover:text-foreground">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }}
                className="flex-1 bg-surface-muted border border-border rounded-xl px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-mono"
                placeholder="Add a tag and press Enter"
              />
              <button type="button" onClick={addTag} className="px-3 py-2 bg-muted hover:bg-border text-foreground rounded-xl text-sm">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground rounded-xl hover:bg-surface-muted font-inter">
            Cancel
          </button>
          <button
            onClick={handleSubmit as any}
            disabled={saving}
            className="px-5 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-sm font-medium rounded-xl font-inter"
          >
            {saving ? "Saving…" : initial ? "Save Changes" : "Add Prompt"}
          </button>
        </div>
      </div>
    </div>
  );
}
